import React, { useEffect, useRef } from 'react';

import { DATE_FORMAT, DATE_TIME_FORMAT } from '@cheyes-weiertal/shared';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Progress } from 'antd';
import dayjs from 'dayjs';
import prettyBytes from 'pretty-bytes';
import { StringParam, useQueryParam } from 'use-query-params';

import { useJobId, useRunningJob } from 'hooks';
import { useImportPhotosByDate } from 'services/api/rest/import';
import { Job as JobType } from 'services/api/rest/jobs';
import { useAdminPhotoById } from 'services/api/rest/photos';

import AbortJobAction from './AbortJobAction';
import * as S from './JobSc';

interface JobProps {
  job: JobType;
}

const JobDetail: React.FC<JobProps> = ({ job }) => {
  const { jobData, isRunning, percent } = useRunningJob(job);
  const { setJobId } = useJobId();
  const [, setDate] = useQueryParam('date', StringParam);
  const queryClient = useQueryClient();

  const photoQuery = useAdminPhotoById(jobData.lastSuccessfullPhotoId || 0, { enabled: false });
  const { current: photoQueryRef } = useRef(photoQuery);

  const importMutation = useImportPhotosByDate({
    onSuccess: newJob => {
      setJobId(newJob.id);
      queryClient.refetchQueries(['jobs']);
    }
  });

  const handleRunAgain = () => {
    setDate(job.dateToFetch);
    importMutation.mutate(dayjs(job.dateToFetch));
  };

  useEffect(() => {
    if (!jobData.lastSuccessfullPhotoId) return;

    photoQueryRef.refetch();
  }, [jobData.lastSuccessfullPhotoId, photoQueryRef]);

  return (
    <S.SingleItem>
      <h3>
        <span>
          Job {jobData.id} - {dayjs(jobData.dateToFetch).format(DATE_FORMAT)}
        </span>
        <span>{dayjs(jobData.createdAt).local().format(DATE_TIME_FORMAT)}</span>
      </h3>
      <div>
        <p>
          <span>Import date:</span>
          <span>{dayjs(jobData.dateToFetch).format(DATE_FORMAT)}</span>
        </p>
      </div>
      <div>
        <p>
          <span>Status:</span>
          <span>{jobData.status}</span>
        </p>
        {isRunning && (
          <S.Progress>
            <Progress percent={percent} />
            <div className="status">{jobData.currentPhotoName}</div>
          </S.Progress>
        )}
      </div>
      <div>
        <p>
          <span>Total:</span>
          <span>{jobData.transformTotal}</span>
        </p>
        <S.Status>
          <span>Success:</span>
          <span className="status success">{jobData.transformSuccessful}</span>
        </S.Status>
        <S.Status>
          <span>Failed:</span>
          <span className="status error">{jobData.transformFailed}</span>
        </S.Status>
        <S.Status>
          <span>Other:</span>
          <span className="status other">{jobData.transformOther}</span>
        </S.Status>
      </div>
      <div>
        <p>
          <span>Created:</span>
          <span>{dayjs(jobData.createdAt).local().format(DATE_TIME_FORMAT)}</span>
        </p>
        <p>
          <span>Updated:</span>
          <span>{dayjs(jobData.updatedAt).local().format(DATE_TIME_FORMAT)}</span>
        </p>
      </div>

      <div className="actions">
        <AbortJobAction job={job} mode="single" />

        <Button
          className="redo"
          icon={<FontAwesomeIcon icon={solid('sync')} />}
          onClick={handleRunAgain}
        >
          Run again
        </Button>

        <Button className="delete" icon={<FontAwesomeIcon icon={solid('trash')} />}>
          Delete
        </Button>
      </div>

      {photoQuery.data && (
        <S.LastImport>
          <p>Last import</p>
          <img width="100%" src={photoQuery.data?.filePath} alt={photoQuery.data.name} />

          <div>
            <p>{photoQuery.data.name}</p>
            <p>{prettyBytes(photoQuery.data.fileSize)}</p>
          </div>
        </S.LastImport>
      )}
    </S.SingleItem>
  );
};
export default JobDetail;
